import React, { useCallback, useEffect, useState } from 'react';
import { Empty, Spin, Tag, Alert, Button, Tooltip } from 'antd';
import { ReloadOutlined, DownloadOutlined, AudioOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import mobileService from '../../../../services/mobileService';
import DocumentPreviewModal from '../DocumentPreviewModal';
import VignetteDocument from './VignetteDocument';
import MemoVocal from './MemoVocal';
import { formatDate } from '../../../../variables/constants';

/**
 * Pieces d'un dossier mobile, vues par un agent.
 *
 * Servi par `GET /v1/mobile-backoffice/declarations/{id}/documents`, qui exige le
 * canal MOBILE : un dossier back-office renvoie 400, pas une liste vide.
 *
 * Deux cas que le backend documente explicitement et qu'il faut afficher, jamais
 * masquer :
 *   - `ossanGedDocumentId` NUL - indexation GED en cours, ou moteur indisponible et
 *     piece conservee en repli local. La ligne existe, le fichier n'est pas encore
 *     telechargeable. Un lien mort serait pire qu'un message.
 *   - `estEnregistrementSonore` - memo vocal, reconnu au type MIME. Il se lit sur
 *     place plutot que de forcer un telechargement.
 */

const formaterTaille = (octets, t) => {
  if (!octets || octets < 0) return null;
  if (octets < 1024) return t('piecesMobiles.tailleOctets', { n: octets });
  if (octets < 1024 * 1024) return t('piecesMobiles.tailleKo', { n: Math.round(octets / 1024) });
  return t('piecesMobiles.tailleMo', { n: (octets / (1024 * 1024)).toFixed(1) });
};

export default function PiecesMobiles({ sinistreTrackingId, titre: titreRecu }) {
  const { t } = useTranslation('dossierSinistre');
  const titre = titreRecu === undefined ? t('piecesMobiles.titreParDefaut') : titreRecu;
  const [docs, setDocs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [apercu, setApercu] = useState(null);

  const charger = useCallback(async () => {
    if (!sinistreTrackingId) return;
    setChargement(true);
    setErreur(null);
    try {
      const liste = await mobileService.documentsDeclaration(sinistreTrackingId);
      setDocs(Array.isArray(liste) ? liste : []);
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('dossierSinistre:piecesMobiles.chargementImpossible'));
    } finally {
      setChargement(false);
    }
  }, [sinistreTrackingId]);

  useEffect(() => { charger(); }, [charger]);

  const telecharger = async (doc) => {
    try {
      const blob = await mobileService.telechargerDocument(doc.ossanGedDocumentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.titre || 'piece';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('piecesMobiles.telechargementImpossible'));
    }
  };

  if (chargement) {
    return <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>;
  }

  if (erreur) {
    return (
      <Alert
        type="error"
        showIcon
        message={erreur}
        action={<Button size="small" icon={<ReloadOutlined />} onClick={charger}>{t('declarationMobile.reessayer')}</Button>}
      />
    );
  }

  if (!docs.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('piecesMobiles.aucunePiece')}
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>
          {titre} <span style={{ color: '#6b7280', fontWeight: 400 }}>({docs.length})</span>
        </span>
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={charger} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {docs.map((doc) => {
          const cle = doc.documentTrackingId || doc.ossanGedDocumentId;
          const indisponible = doc.ossanGedDocumentId == null;
          const previsualisable = !indisponible
            && ((doc.mimeType || '').startsWith('image/') || (doc.mimeType || '').includes('pdf'));
          const taille = formaterTaille(doc.taille, t);

          // Le memo vocal occupe toute la largeur : un lecteur audio dans une carte
          // de 190px serait inutilisable.
          if (doc.estEnregistrementSonore) {
            return (
              <div
                key={cle}
                style={{
                  width: '100%', border: '1px solid #ddd6fe', background: '#f5f3ff',
                  borderRadius: 10, padding: 12,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6', marginBottom: 8 }}>
                  <AudioOutlined /> {doc.titre || t('piecesMobiles.memoVocal')}
                  {doc.dateDepot && (
                    <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>
                      {t('piecesMobiles.depose', { date: formatDate(doc.dateDepot).replace(/^Le /, 'le ') })}
                    </span>
                  )}
                </div>
                {indisponible
                  ? <span style={{ fontSize: 12, color: '#b45309' }}>
                    {t('piecesMobiles.fichierPasDisponible', { statut: doc.statutIndexation || t('piecesMobiles.indexationEnCoursMinuscule') })}
                  </span>
                  : <MemoVocal gedRef={doc.ossanGedDocumentId} />}
              </div>
            );
          }

          return (
            <div
              key={cle}
              style={{
                width: 200, border: '1px solid #e5e7eb', borderRadius: 10,
                padding: 10, background: '#fff', display: 'flex', gap: 10,
              }}
            >
              <button
                type="button"
                onClick={() => previsualisable && setApercu(doc)}
                style={{
                  border: 'none', background: 'none', padding: 0,
                  cursor: previsualisable ? 'zoom-in' : 'default',
                }}
                title={previsualisable ? t('piecesMobiles.previsualiser') : t('piecesMobiles.apercuIndisponible')}
              >
                <VignetteDocument doc={doc} />
              </button>

              <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Tooltip title={doc.titre}>
                  <div style={{
                    fontSize: 12, fontWeight: 650, color: '#111827',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {doc.titre || t('piecesMobiles.pieceJointe')}
                  </div>
                </Tooltip>
                {doc.typeDocument && (
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{doc.typeDocument}</div>
                )}
                {doc.deposePar && (
                  <Tooltip title={t('piecesMobiles.deposePar', { nom: doc.deposePar })}>
                    <div style={{
                      fontSize: 10.5, color: '#9ca3af',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {doc.deposePar}
                    </div>
                  </Tooltip>
                )}
                {indisponible && (
                  <Tag color="orange" style={{ borderRadius: 12, fontSize: 10, marginInlineEnd: 0, alignSelf: 'flex-start' }}>
                    {doc.statutIndexation || t('piecesMobiles.indexationEnCours')}
                  </Tag>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {taille && <span style={{ fontSize: 10.5, color: '#9ca3af' }}>{taille}</span>}
                  <Button
                    size="small"
                    type="text"
                    icon={<DownloadOutlined />}
                    disabled={indisponible}
                    onClick={() => telecharger(doc)}
                    style={{ marginLeft: 'auto' }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <DocumentPreviewModal doc={apercu} open={!!apercu} onClose={() => setApercu(null)} />
    </div>
  );
}
